import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router'
import type { AnyRoute } from '@tanstack/react-router'
import { i18n } from '../../shared/i18n/i18n'
import sv from './locales/sv.json'
import en from './locales/en.json'

i18n.addResourceBundle('sv', 'seasons', sv, true, true)
i18n.addResourceBundle('en', 'seasons', en, true, true)

export function buildSeasonsRoutes<TParent extends AnyRoute>(parent: TParent) {
  return [
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons',
      component: lazyRouteComponent(
        () => import('./SeasonsPage'),
        'SeasonsPage',
      ),
    }),
    // Bare /seasons/$seasonId → /overview redirect so URLs without a tab
    // suffix still land somewhere sensible.
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId',
      beforeLoad: ({ params }) => {
        const { seasonId } = params as { seasonId: string }
        throw redirect({ to: `/seasons/${encodeURIComponent(seasonId)}/overview` })
      },
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/overview',
      component: lazyRouteComponent(
        () => import('./SeasonOverviewPage'),
        'SeasonOverviewPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/reports',
      component: lazyRouteComponent(
        () => import('./SeasonReportsPage'),
        'SeasonReportsPage',
      ),
    }),
    // Övrigt vilt — reports on species outside the shooting plan (CLUB-131).
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/otherReports',
      component: lazyRouteComponent(
        () => import('./SeasonOtherReportsPage'),
        'SeasonOtherReportsPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/availableGame',
      component: lazyRouteComponent(
        () => import('./SeasonAvailableGamePage'),
        'SeasonAvailableGamePage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/quotas',
      component: lazyRouteComponent(
        () => import('./SeasonQuotasPage'),
        'SeasonQuotasPage',
      ),
    }),
    // Bare /seasons/$id/allocation → /allocation/teams
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/allocation',
      beforeLoad: ({ params }) => {
        const { seasonId } = params as { seasonId: string }
        throw redirect({ to: `/seasons/${encodeURIComponent(seasonId)}/allocation/teams` })
      },
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/allocation/$allocationType',
      component: lazyRouteComponent(
        () => import('./SeasonAllocationPage'),
        'SeasonAllocationPage',
      ),
    }),
    // Games — list + per-game detail.
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/game',
      component: lazyRouteComponent(
        () => import('./SeasonGamesPage'),
        'SeasonGamesPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/game/$gameId',
      component: lazyRouteComponent(
        () => import('./SeasonGameDetailPage'),
        'SeasonGameDetailPage',
      ),
    }),
    // Observables — same data source as Games (filter applied client-side
    // per legacy behaviour), with their own breadcrumb/back-target.
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/observables',
      component: lazyRouteComponent(
        () => import('./SeasonObservablesPage'),
        'SeasonObservablesPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/observables/$gameId',
      component: lazyRouteComponent(
        () => import('./SeasonObservableDetailPage'),
        'SeasonObservableDetailPage',
      ),
    }),
    // Observation reports — 3-level drill-down: teams summary → one team's
    // reports → single report detail. Mirrors legacy URLs exactly.
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/observationReports',
      component: lazyRouteComponent(
        () => import('./SeasonObservationReportsPage'),
        'SeasonObservationReportsPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/observationReports/$teamId',
      component: lazyRouteComponent(
        () => import('./SeasonObservationTeamReportsPage'),
        'SeasonObservationTeamReportsPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/observationReports/$teamId/reports/$reportId',
      component: lazyRouteComponent(
        () => import('./SeasonObservationReportDetailPage'),
        'SeasonObservationReportDetailPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/observationReports/$teamId/reports/new',
      component: lazyRouteComponent(
        () => import('./SeasonObservationReportFormPage'),
        'SeasonObservationReportFormPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/observationReports/$teamId/reports/$reportId/edit',
      component: lazyRouteComponent(
        () => import('./SeasonObservationReportFormPage'),
        'SeasonObservationReportFormPage',
      ),
    }),
    // Final reports — per-team season summaries. Read-only this slice;
    // submit/edit flow lands in a follow-up.
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/finalReports',
      component: lazyRouteComponent(
        () => import('./SeasonFinalReportsPage'),
        'SeasonFinalReportsPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/finalReports/$teamId',
      component: lazyRouteComponent(
        () => import('./SeasonFinalReportDetailPage'),
        'SeasonFinalReportDetailPage',
      ),
    }),
    // Operational tabs: handout, SMS codes, notifications, external reports.
    // The legacy URLs use `sms`, `externalreports` (lowercase) — kept
    // verbatim so existing bookmarks port over.
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/handout',
      component: lazyRouteComponent(
        () => import('./SeasonHandoutPage'),
        'SeasonHandoutPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/sms',
      component: lazyRouteComponent(
        () => import('./SeasonSmsCodesPage'),
        'SeasonSmsCodesPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/notifications',
      component: lazyRouteComponent(
        () => import('./SeasonNotificationsPage'),
        'SeasonNotificationsPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/externalreports',
      component: lazyRouteComponent(
        () => import('./SeasonExternalReportsPage'),
        'SeasonExternalReportsPage',
      ),
    }),
    // Killing fees has three sub-tabs (fees / invoices / billing). The
    // bare /killingFees path redirects to /fees to preserve old bookmarks.
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/killingFees',
      beforeLoad: ({ params }) => {
        const { seasonId } = params as { seasonId: string }
        throw redirect({ to: `/seasons/${encodeURIComponent(seasonId)}/killingFees/fees` })
      },
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/killingFees/fees',
      component: lazyRouteComponent(
        () => import('./SeasonKillingFeesFeesPage'),
        'SeasonKillingFeesFeesPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/killingFees/invoices',
      component: lazyRouteComponent(
        () => import('./SeasonKillingFeesInvoicesPage'),
        'SeasonKillingFeesInvoicesPage',
      ),
    }),
    createRoute({
      getParentRoute: () => parent,
      path: '/seasons/$seasonId/killingFees/billing',
      component: lazyRouteComponent(
        () => import('./SeasonKillingFeesBillingPage'),
        'SeasonKillingFeesBillingPage',
      ),
    }),
  ]
}
